'use client';

import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import gfm from 'remark-gfm';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionSquare } from '@fortawesome/pro-solid-svg-icons';

function convertPhoneNumbers(text = "") {
    const regex = /\+\d{2} ?\-\(0\)\d{1,3}\-\d{3} \d{2} \d{2}/g;
    return text.replace(regex, (match) => {
        const telLink = match.replace(/[()]/g, '').replace(/\s/g, '');
        return `<a href="tel:${telLink}">${match}</a>`;
    });
}

export const UserEntry = ({ entry }) => {
    const processedText = convertPhoneNumbers(entry.message);
    return (
        <div className="text-right">
            <div className="ml-5 relative inline-flex">
                <div className="rounded-xl bg-sky-500 text-white px-3 py-1.5 z-10 text-left">
                    <ReactMarkdown
                        remarkPlugins={[gfm]}
                        components={{
                            a: CustomLink,
                            ul: ({ node, ...props }) => <ul style={{ margin: '0.5em', listStylePosition: 'inside' }} {...props} />,
                            ol: ({ node, ...props }) => <ol style={{ listStylePosition: 'inside' }} {...props} />,
                            li: ({ node, ...props }) => <li style={{ marginBottom: '0.5em' }} {...props} />,
                            p: ({ node, ...props }) => <p style={{ display: 'inline' }} {...props} />
                        }}
                        rehypePlugins={[rehypeSanitize]}
                    >
                        {processedText}
                    </ReactMarkdown>
                </div>
                <div className="absolute -right-2 -bottom-0.5 rounded-full bg-sky-500 w-6 h-6"></div>
                <div className="absolute -right-3 bottom-0.5 rounded-full bg-white w-3 h-8"></div>
            </div>
        </div>
    )
}

export const BotEntry = ({ entry, isFirstBotMessage, customDisclaimerText }) => {
    // Default disclaimer text with option to override with custom text
    const disclaimerText = customDisclaimerText || "I am an AI assistant. I aim to be helpful, but please verify any important information.";
    const processedWithPhoneNumbers = convertPhoneNumbers(entry.message);

    const CustomSmall = ({ children }) => (
        <div className="border-t border-gray-300 mt-1 leading-3">
            <span style={{ fontSize: "0.75em", color: "gray" }}>{children}</span>
        </div>
    );

    return (
        <div className="text-left my-2">
            <div className="mr-5 relative inline-flex">
                <div className="rounded-xl bg-neutral-200 text-black px-3 py-1.5 z-10">
                    <div className='mb-3'>
                        <ReactMarkdown
                            remarkPlugins={[gfm]}
                            components={{
                                a: CustomLink,
                                ul: ({ node, ...props }) => <ul style={{ listStylePosition: 'inside' }} {...props} />,
                                ol: ({ node, ...props }) => <ol style={{ listStylePosition: 'inside' }} {...props} />,
                                li: ({ node, ...props }) => <li style={{ marginBottom: '0.5em' }} {...props} />,
                                p: ({ node, ...props }) => <p style={{ display: 'inline' }} {...props} />,
                                hr: ({ node, ...props }) => <hr className="my-2 border-neutral-300" {...props} />,
                                small: CustomSmall,
                            }}
                            rehypePlugins={[rehypeSanitize]}
                        >
                            {processedWithPhoneNumbers}
                        </ReactMarkdown>
                    </div>

                    <CustomSmall>{disclaimerText}</CustomSmall>
                </div>
                <div className="absolute -left-2 -bottom-0.5 rounded-full bg-neutral-200 w-6 h-6"></div>
                <div className="absolute -left-3 bottom-0.5 rounded-full bg-white w-3 h-8"></div>
            </div>
        </div>
    );
};

export const CustomLink = (props) => {
    const className = 'font-bold text-witnium-purple underline cursor-pointer'

    if (props.href) {
        if (props.href.startsWith('mailto:') || props.href.startsWith('tel:')) {
            return (
                <a href={props.href + '?utm_source=chattrick'} className={className}>
                    {props.children}
                </a>
            );
        }
    }

    return (
        <a href={props.href} target="_blank" rel="noopener noreferrer" className={className}>
            {props.children}
        </a>
    );
};

export const FollowUp = ({ question, onSendFollowUp, busy }) => {
    return (
        <div
            onClick={() => { onSendFollowUp(question); }}
            disabled={busy}
            className={twMerge(
                "block w-full text-left rounded-md py-1.5 px-4 mb-2 font-normal text-xs sm:text-sm bg-lime-100 border border-lime-300 hover:bg-lime-200 cursor-pointer",
                busy ? "opacity-70" : ""
            )}
        >
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faQuestionSquare} className="text-2xl text-lime-400" />
                <p className="w-full text-black break-words whitespace-normal overflow-wrap-normal">{question}</p>
            </div>
        </div>
    )
}